import { Box, Row, Stack, Title } from "components";
import QRCode from "react-qr-code";
import { useGetQuery, usePostQuery } from "hooks";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSocket } from "providers";
import { SongList } from "../../components";
import { Canvas, Puck, QRContainer, Player, MiniQROverlay } from "./styles";

const SongPlayer = ({
  item: {
    id,
    details: { song, user, data },
  },
  onSongEnd = () => true,
  qrValue,
  isPaused,
  shouldRestart,
  onRestarted,
}) => {
  const vid = useRef();

  const { post } = usePostQuery({ method: "DELETE", url: "" });

  const endSong = (event) => {
    post({}, `/host/queue_items/${id}`);
    onSongEnd(id);
  };

  useEffect(() => {
    if (!vid.current) return;
    vid?.current?.addEventListener("ended", endSong);
    return () => vid?.current?.removeEventListener("ended", endSong);
  }, [vid]);

  useEffect(() => {
    console.log("I should play in 20 seconds");
    setTimeout(() => vid?.current?.play(), 20000);
  }, [song]);

  useEffect(() => {
    if (!vid.current) return;
    if (isPaused) {
      vid.current.pause();
    } else {
      vid.current.play();
    }
  }, [isPaused]);

  useEffect(() => {
    if (shouldRestart && vid.current) {
      vid.current.currentTime = 0;
      vid.current.play();
      onRestarted();
    }
  }, [shouldRestart]);

  return (
    <>
      <Player ref={vid}>
        <source
          src={`${process.env.API_URL}/songs/${song.id}.${data.ext}`}
          type={`video/${data.ext === "mkv" ? "mp4" : data.ext}`}
        />
      </Player>
      <MiniQROverlay>
        <QRCode
          size={64}
          style={{ height: "auto", width: "5vw", minWidth: "60px" }}
          value={qrValue}
          viewBox={"0 0 64 64"}
        />
      </MiniQROverlay>
    </>
  );
};

const ScreenSaver = () => {
  const { slug } = useParams();
  const [state, setState] = useState(
    `${document.location.host}/guest/karaoke/${slug}`
  );
  return (
    <Puck>
      <QRContainer>
        <QRCode
          size={128}
          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
          value={state}
          viewBox={"0 0 128 128"}
        />
      </QRContainer>
    </Puck>
  );
};

export const HostKaraokeDisplay = () => {
  const [song, setSong] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [shouldRestart, setShouldRestart] = useState(false);

  const socket = useSocket();
  const { slug } = useParams();

  const { post } = usePostQuery({ method: "DELETE", url: "" });

  const { data, error, isFetching, reFetch } = useGetQuery({
    url: `/host/queue/${slug}`,
  });

  // Sync local state with server data
  useEffect(() => {
    if (data?.queue?.QueueItems) {
      setQueueItems(data.queue.QueueItems);
    }
  }, [data?.queue]);

  useEffect(() => {
    const items = queueItems || [];
    if (items.length > 0 && song?.id !== items[0].id) {
      setSong(null);
      if (items[0].state === "completed") return;
      setTimeout(() => setSong(items[0]), 15000);
    }
  }, [queueItems]);

  useEffect(() => {
    socket.on(`/karaoke/${slug}`, (res) => {
      reFetch(true);
    });
    socket.on(`/karaoke/${slug}/control`, (res) => {
      if (res.paused !== undefined) {
        setIsPaused(res.paused);
      }
      if (res.restart) {
        setShouldRestart(true);
      }
      if (res.skip && song) {
        // Delete the current song from the queue and remove the video player
        post({}, `/host/queue_items/${song.id}`);
        setSong(null);
        reFetch(true);
      }
    });
    return () => {
      socket.off(`/karaoke/${slug}`);
      socket.off(`/karaoke/${slug}/control`);
    };
  }, [socket, slug, song]);

  const qrValue = `${document.location.host}/guest/karaoke/${slug}`;

  return (
    <Canvas>
      {song ? (
        <SongPlayer
          onSongEnd={() => reFetch(true)}
          item={song}
          qrValue={qrValue}
          isPaused={isPaused}
          shouldRestart={shouldRestart}
          onRestarted={() => setShouldRestart(false)}
        />
      ) : null}
      <Row>
        <Box>
          <Title style={{ margin: "1rem" }} level="3">
            {data?.queue?.name}
          </Title>
          <ScreenSaver />
        </Box>
        <Stack>
          <Title style={{ margin: "1rem" }} level="4">
            Up Next
          </Title>
          <SongList songs={queueItems} />
        </Stack>
      </Row>
    </Canvas>
  );
};
